/*
 * Copyright (c)  CYBERKOALA LLC 2025.
 * Proprietary License.
 * Redistribution and use in source and binary forms, with or without
 * modification, are restricted.
 */

import React, { useEffect, useState } from 'react';
import { isPardusBrowser } from '@/components/check-user-agent.tsx';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '@/config.ts';
import { Server } from '@/models';
import { HelpCircle } from 'lucide-react';

/**
 * Changes title injected in Electron depending on route: Friends, etc
 *
 * @constructor
 */
export const Title = () => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [icon, setIcon] = useState('');

  const staticRouteTitleMap = (t: any) => ({
    '/channels/@me/friends': t('routes.friends'),
    '/channels/@me': t('routes.messages'),
    '/channels/@me/message-requests': t('routes.message_requests'),
    '/discovery': t('routes.discovery'),
    '/channels/@me/desktop': t('routes.desktop_app'),
    '/channels/@me/account': t('routes.account'),
    '/channels/@me/change-password': t('routes.change_password'),
    '/channels/@me/devices': t('routes.devices'),
    '/channels/@me/subscriptions': t('routes.subscriptions'),
    '/channels/@me/boost': t('routes.subscriptions'),
    '/channels/@me/appearance': t('routes.appearance'),
    '/channels/@me/voice-video': t('routes.voice_video_settings'),
    '/channels/@me/notifications': t('routes.notifications'),
    '/channels/@me/keybinds': t('routes.keybinds'),
    '/channels/@me/language': t('routes.language'),
    '/channels/@me/advanced': t('routes.advanced'),
    '/channels/@me/gamesdb': t('routes.gamesdb'),
    '/channels/@me/games-library': t('routes.games_library'),
    '/channels/@me/activity-privacy': t('routes.activity_privacy'),
    '/legal/privacy': t('routes.privacy_policy'),
    '/legal/terms': t('routes.terms_of_use'),
  });

  const parseUrl = () => {
    const path = window.location.pathname;
    const segments = path.split('/').filter(Boolean);

    let serverId: string | null = null;
    let channelId: string | null = null;

    const serverIndex = segments.indexOf('servers');
    if (serverIndex !== -1 && segments[serverIndex + 1]) {
      serverId = segments[serverIndex + 1];
    }

    const channelIndex = segments.indexOf('channels');
    if (channelIndex !== -1 && segments[channelIndex + 1]) {
      channelId = segments[channelIndex + 1];
    }

    return { path, serverId, channelId };
  };

  useEffect(() => {
    const updateTitle = async () => {
      const { path, serverId, channelId } = parseUrl();
      const staticTitles = staticRouteTitleMap(t);
      const staticMatch = Object.entries(staticTitles)
        .sort(([a], [b]) => b.length - a.length)
        .find(([key]) => path.includes(key));

      if (staticMatch) {
        setTitle(staticMatch[1]);
        document.title = `${staticMatch[1]} | CyberKoala® Calls`;
        return;
      }

      if (serverId) {
        try {
          const res = await fetch(`${API_BASE}/api/servers/${serverId}`, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
          });
          const server: Server = await res.json();
          if (channelId) {
            const channel = server.channels.find(
              (c: any) => c.id === channelId
            );
            const newTitle = `${server.name} – ${channel?.name ?? ''}`;
            setTitle(newTitle);
            document.title = `${newTitle} | CyberKoala® Calls`;
          } else {
            setTitle(server.name);
            document.title = `${server.name} | CyberKoala® Calls`;
          }
          if (server.imageUrl) {
            setIcon(server.imageUrl);
          }
        } catch {
          setTitle('');
          setIcon('');
        }
      } else {
        setTitle('');
        setIcon('');
      }
    };

    updateTitle();

    const handlePopState = () => updateTitle();
    window.addEventListener('popstate', handlePopState);

    const originalPush = history.pushState;
    history.pushState = function (...args) {
      originalPush.apply(this, args as any);
      updateTitle();
    };
    const originalReplace = history.replaceState;
    history.replaceState = function (...args) {
      originalReplace.apply(this, args as any);
      updateTitle();
    };

    return () => {
      window.removeEventListener('popstate', handlePopState);
      history.pushState = originalPush;
      history.replaceState = originalReplace;
    };
  }, [t]);

  if (!window) return null;

  if (isPardusBrowser())
    return (
      <div className="titlebar flex items-center justify-center relative h-10 px-4">
        <div className="absolute left-4 flex items-center text-sm">
          <img src="/logo.png" alt="Calls Logo" className="size-5 mr-2" />
          <span>
            CyberKoala<sup className="text-muted-foreground">®</sup> Calls
          </span>
        </div>

        {/*<div className="flex-1" />*/}
        <div className="flex items-center space-x-1">
          {icon && (
            <img
              src={icon}
              width={16}
              height={16}
              alt="serverImage"
              className="rounded-sm"
            />
          )}
          {title ? (
            <span className="pl-1">{title}</span>
          ) : (
            <span>
              CyberKoala<sup className="text-muted-foreground">®</sup> Calls
            </span>
          )}
        </div>
      </div>
    );
  else return null;
};
